import { KONSTRUKCJE, KONSTRUKCJA_DOMYSLNA, konstrukcjaWg, kosztKonstrukcji } from '../konstrukcja'
import { kosztMontazu } from '../../config/cennikMontazu'
import { magazynZPozycji, montazMagazynuZProgow, przeliczWycene, stawkaMontazuZProgow, wybraneUslugi } from '../wycena'
import { kwota } from './handel'

/**
 * Orientacyjna cena całości zestawu klienta (prośba właściciela 2026-10-11, runda 3, pakiet B).
 *
 * Ta sama mechanika co karta zestawu w asystencie (`hooks/useKosztZestawu`): sprzęt (ceny sklepów, brutto,
 * z BMS wycenionym poza pozycjami) i dostawa z zestawu, konstrukcja z `kosztKonstrukcji` (rodzaj z karty klienta,
 * `dane.konstrukcja = { typ }`, moc modułu z zestawu), montaż z progów firmy (zł/kWp, a bez progu nasz cennik),
 * potem `przeliczWycene`: marża z tabeli firmy i osobny VAT dla towaru i montażu. Bez ustawień firmy
 * (konto prywatne) liczy jak serwis zawsze: bez marży, jedna stawka 23%.
 *
 * Moc instalacji: najpierw moc modułów zestawu (cena dotyczy tego sprzętu), a gdy zestaw jej nie ma, moc z karty.
 * Konstrukcja niewybrana: liczymy blachę trapezową (domyślna jak w asystencie) i zaznaczamy to w wyniku,
 * bo cena bez konstrukcji byłaby zaniżona.
 *
 * Narzut netto (pole handlowca przy „Cena dla klienta”): doliczany do netto, a VAT od niego w stawce montażu
 * (`vat_rate` firmy), czyli tej, w której firma fakturuje klientowi usługę instalacji; z tą stawką zapisuje się
 * też cena. Narzut to dopłata do ceny usługi u klienta, a nie zakup towaru, więc stawka sprzętu (23%) zawyżałaby
 * cenę na budynku mieszkalnym. Marża firmy nie liczy się od narzutu.
 *
 * Magazyn energii (runda 6, 2026-10-12): gdy zestaw ma magazyn, a firma ma ustawienia, dochodzi montaż magazynu
 * (zł/kWh z `storage_install_ranges` razy pojemność, jak montaż PV) i marża za magazyn (`storage_margin_ranges`,
 * próg po kWh). Usługi dodatkowe (`uslugi`: id z `extra_services` firmy, w karcie `dane.cena.uslugi`) doliczane
 * netto z własną stawką VAT każdej usługi. Bez magazynu i bez usług kwota jest taka sama jak przed rundą 6.
 *
 * Kwoty składników zwracamy, ale interfejs handlowca pokazuje tylko cenę końcową i listę tego, co w niej jest;
 * rozbicie na kwoty widzi właściciel firmy (`pokazRozbicie` w `CenaITerminy`).
 */

/** Klucz konstrukcji z karty klienta albo null (nieznany klucz też null). */
export const typKonstrukcji = (konstrukcja) => {
  const typ = konstrukcja && typeof konstrukcja === 'object' ? konstrukcja.typ : null
  return KONSTRUKCJE.some((k) => k.key === typ) ? typ : null
}

/** Nazwa rodzaju konstrukcji do listy sprzętu. */
export const nazwaKonstrukcji = (typ) => (typ ? konstrukcjaWg(typ).label : '')

const dodatnia = (v) => {
  const x = kwota(v)
  return x != null && x > 0 ? x : null
}

/** Moc modułów zestawu w kWp albo null. */
export const kwpZestawu = (zestaw) => dodatnia(zestaw?.payload?.summary?.panels_total_kwp)

/** Moc jednego modułu zestawu w W (konstrukcja kosztuje od sztuki modułu) albo null. */
export const mocModuluZestawu = (zestaw) => dodatnia((zestaw?.payload?.items || []).find((i) => i.category === 'panels')?.spec?.power_w)

/**
 * Sprzęt i dostawa zestawu, brutto, albo null, gdy zestaw nie ma cen.
 * BMS: silnik wlicza go w pozycje, a `bms.price_pln` jest tylko wtedy, gdy wyceniono go osobno (jak `VariantCard`).
 */
export const sprzetZestawu = (zestaw) => {
  const p = zestaw?.payload || {}
  const t = p.totals || {}
  const produkty = dodatnia(t.products_pln)
  if (produkty != null) {
    const bms = p.bms?.resolved ? (kwota(p.bms.price_pln) || 0) : 0
    return { sprzetZl: produkty + bms, dostawaZl: kwota(t.shipping_pln) || 0 }
  }
  // Starsze zapisy bez rozbicia: kwota zestawu jako sprzęt (dostawy nie da się z niej wydzielić).
  const razem = dodatnia(t.total_pln) ?? dodatnia(zestaw?.total_pln)
  return razem != null ? { sprzetZl: razem, dostawaZl: 0 } : null
}

/** Krótki powód, dla którego zestaw nie ma ceny sprzętu. */
export const powodBrakuCen = (zestaw) => {
  const wariant = zestaw?.payload?.variant
  if (!zestaw) return 'Brak zestawu z cenami sprzętu.'
  if (wariant === 'projekt_3d') return 'Zestaw z Projektu 3D nie ma cen sprzętu. Zapisz projekt w karcie jeszcze raz, pobierzemy ceny z katalogu.'
  if (wariant === 'dobor_reczny') return 'Część sprzętu nie ma ceny w sklepach.'
  return 'Zestaw nie ma cen sprzętu.'
}

/**
 * Wycena zestawu dla klienta.
 *
 * @param {Object} p
 * @param {Object} [p.zestaw]      zapisany zestaw (`{ payload, total_pln }`)
 * @param {number} [p.kwp]         moc z karty klienta, gdy zestaw jej nie ma
 * @param {Object} [p.konstrukcja] `dane.konstrukcja` (`{ typ }`)
 * @param {Object} [p.ustawienia]  ustawienia wyceny firmy (`/account/quote-settings`) albo null
 * @param {number|string} [p.sprzetZl] sprzęt brutto wpisany ręcznie, gdy zestaw nie ma cen
 * @param {number|string} [p.narzutNetto] narzut handlowca netto
 * @param {string[]} [p.uslugi]    id usług dodatkowych firmy wybranych dla klienta
 * @returns {{ ok: false, powod: string } | { ok: true, bruttoZl, nettoZl, kwp, vatFaktury, konstrukcja, sklad, skladniki }}
 */
export const wycenaKlienta = ({ zestaw = null, kwp: kwpKarty = null, konstrukcja = null, ustawienia = null, sprzetZl = null, narzutNetto = 0, uslugi = [] } = {}) => {
  const zZestawu = sprzetZestawu(zestaw)
  const reczny = dodatnia(sprzetZl)
  const sprzet = zZestawu || (reczny != null ? { sprzetZl: reczny, dostawaZl: 0 } : null)
  if (!sprzet) return { ok: false, powod: powodBrakuCen(zestaw) }

  const kwp = kwpZestawu(zestaw) ?? dodatnia(kwpKarty)
  if (kwp == null) return { ok: false, powod: 'Brak mocy PV: wpisz ją w danych klienta albo podepnij zestaw z modułami.' }

  const typ = typKonstrukcji(konstrukcja)
  const typLiczony = typ || KONSTRUKCJA_DOMYSLNA
  const konstrukcjaZl = kosztKonstrukcji({ kwp, konstrukcja: typLiczony, mocModuluW: mocModuluZestawu(zestaw) })?.razemZl || 0

  // Stawka montażu firmy ma pierwszeństwo; pusty próg to nasz cennik, nie „montaż za darmo” (`useKosztZestawu`).
  const stawkaFirmy = ustawienia ? stawkaMontazuZProgow(ustawienia.install_ranges, kwp) : null
  const montazZl = stawkaFirmy != null ? Math.round(stawkaFirmy * kwp) : (kosztMontazu(kwp) || 0)

  // Magazyn i usługi tylko z ustawień firmy: bez nich (konto prywatne) liczymy jak serwis zawsze.
  const magazyn = ustawienia ? magazynZPozycji(zestaw?.payload?.items, zestaw?.payload?.summary) : { kwh: null, cenaZl: 0 }
  const montazMagazynu = magazyn.kwh ? montazMagazynuZProgow(ustawienia.storage_install_ranges, magazyn.kwh) : null
  const listaUslug = ustawienia ? wybraneUslugi(ustawienia.extra_services, uslugi) : []

  const w = przeliczWycene({
    sprzetZl: sprzet.sprzetZl, dostawaZl: sprzet.dostawaZl, konstrukcjaZl, montazZl, kwp,
    ...(ustawienia ? {
      progiMarzy: ustawienia.margin_ranges, trybMarzy: ustawienia.margin_mode,
      vatSprzetu: ustawienia.source_vat_rate, vatMontazu: ustawienia.vat_rate,
      kwh: magazyn.kwh, magazynZl: magazyn.cenaZl, montazMagazynuZl: montazMagazynu?.kwotaZl || 0,
      progiMarzyMagazynu: ustawienia.storage_margin_ranges, uslugi: listaUslug,
    } : {}),
  })

  const narzut = kwota(narzutNetto) || 0
  const vatFaktury = w.stawkaVatMontazu
  // Netto dla klienta to netto kosztów plus marża (marża nie dostaje podatku, `przeliczWycene`) plus narzut.
  const nettoZl = Math.round(w.nettoZl + w.marzaZl + narzut)
  // Zaokrąglenie raz, na końcu; bez narzutu dokładnie kwota z `przeliczWycene`.
  const bruttoZl = narzut > 0 ? Math.round(w.nettoZl + w.vatZl + w.marzaZl + narzut * (1 + vatFaktury / 100)) : w.bruttoZl

  const sklad = [
    'sprzęt',
    sprzet.dostawaZl > 0 && 'dostawa',
    `konstrukcja (${konstrukcjaWg(typLiczony).label.toLowerCase()})`,
    'montaż',
    montazMagazynu && 'montaż magazynu',
    w.marzaZl > 0 && 'marża firmy',
    ...listaUslug.map((u) => u.nazwa),
    narzut > 0 && 'narzut',
    'VAT',
  ].filter(Boolean)

  return {
    ok: true,
    bruttoZl,
    nettoZl,
    kwp,
    vatFaktury,
    konstrukcja: { typ: typLiczony, etykieta: konstrukcjaWg(typLiczony).label, domyslna: !typ },
    sklad,
    skladniki: {
      sprzet: sprzet.sprzetZl, dostawa: sprzet.dostawaZl, konstrukcja: konstrukcjaZl, montaz: montazZl,
      // Stawka montażu firmy w zł/kWp albo null, gdy montaż liczy nasz cennik (brak progu firmy).
      montazStawka: stawkaFirmy,
      // Magazyn: pojemność, montaż (kwota i stawka zł/kWh) i marża za magazyn; bez magazynu zera i null.
      kwh: magazyn.kwh, montazMagazynu: montazMagazynu?.kwotaZl || 0, montazMagazynuStawka: montazMagazynu?.stawka ?? null,
      // `marza` to cała marża (instalacja i magazyn), `marzaMagazynu` jej część za magazyn.
      marza: Math.round(w.marzaZl), marzaMagazynu: Math.round(w.marzaMagazynuZl || 0),
      uslugi: listaUslug, narzut, sprzetRecznie: !zZestawu,
    },
  }
}

/**
 * Stawka VAT narzutu: stawka montażu firmy (`vat_rate`), jak w trybie „Z asystenta doboru”; bez ustawień firmy 23%,
 * jak `przeliczWycene` bez stawek firmy.
 */
export const stawkaVatNarzutu = (ustawienia) => {
  const v = Number(ustawienia?.vat_rate ?? 23)
  return Number.isFinite(v) ? v : 23
}

const grosze = (x) => Math.round(x * 100) / 100

/**
 * Cena wpisana ręcznie z narzutem (runda 4, 2026-10-11) w kształcie `dane.cena`.
 *
 * `netto`/`brutto`: cena wpisana przez handlowca (w stawce `vat`), `narzut`: narzut netto. Narzut dolicza się do
 * netto, a do brutto z VAT w stawce montażu (`vatNarzutu`, ta sama zasada co w `wycenaKlienta`). W karcie zostaje
 * cena końcowa, a w `skladniki` narzut, jego stawka i cena wpisana, żeby formularz pokazał je po powrocie.
 * Bez narzutu (pusty, zero) kształt jak dotąd: `skladniki: null`. Sam narzut bez ceny nie jest ceną.
 */
export const cenaRecznaZNarzutem = ({ netto = null, brutto = null, vat, narzut = 0, vatNarzutu = 23 }) => {
  const n = kwota(netto)
  const b = kwota(brutto)
  const z = kwota(narzut) || 0
  if (z <= 0 || (n == null && b == null)) return { tryb: 'reczna', netto: n, brutto: b, vat, skladniki: null }
  return {
    tryb: 'reczna',
    netto: n != null ? grosze(n + z) : null,
    brutto: b != null ? grosze(b + z * (1 + vatNarzutu / 100)) : null,
    vat,
    skladniki: { narzut: z, vatNarzutu, cenaNetto: n, cenaBrutto: b },
  }
}

/** Narzut zapisany przy cenie wpisanej ręcznie (`cenaRecznaZNarzutem`) albo null. */
export const narzutCenyRecznej = (cena) => {
  const s = cena?.tryb === 'reczna' ? cena.skladniki : null
  const z = kwota(s?.narzut)
  if (!s || !(z > 0) || !('cenaNetto' in s || 'cenaBrutto' in s)) return null
  return { narzut: z, cenaNetto: kwota(s.cenaNetto), cenaBrutto: kwota(s.cenaBrutto) }
}

/**
 * Czy zapisana cena dla klienta jest niższa od orientacyjnej ceny całości (ostrzeżenie „pod wodę”).
 * Porównujemy brutto, a gdy go nie ma, netto. Bez ceny albo bez wyceny: false.
 */
export const cenaPonizejWyceny = (cena, wycena) => {
  if (!wycena?.ok || !cena) return false
  if (cena.brutto != null) return cena.brutto < wycena.bruttoZl
  if (cena.netto != null) return cena.netto < wycena.nettoZl
  return false
}
